const DEFAULT_PROJECT_VERSION = 2;
const DEFAULT_PROJECT_NAME = "新しいプロジェクト";
const DEFAULT_PLC_PROFILE_ID = "generic-st";
const DEFAULT_CHAT_THREAD_LABEL = "新しいスレッド";
const DEFAULT_STATUS_TEXT = "準備完了";
const DEFAULT_SETTINGS_SUMMARY = "Generic IEC ST / FlowContext v1.0 / IR v1.0 / 0 steps / Skill: 0件";
const DEFAULT_INPUT_PLACEHOLDER = "追加指示を入力";

function defaultNow() {
    return Date.now();
}

function defaultRandom() {
    return Math.random();
}

function defaultNowIso() {
    return new Date().toISOString();
}

export function createChatThreadId(deps = {}) {
    const {
        now = defaultNow,
        random = defaultRandom
    } = deps;
    return `thread-${now()}-${random().toString(36).slice(2, 10)}`;
}

export function createChatThread(deps = {}) {
    const {
        nowIso = defaultNowIso,
        createThreadId = () => createChatThreadId()
    } = deps;
    return {
        id: createThreadId(),
        label: DEFAULT_CHAT_THREAD_LABEL,
        messages: [],
        updatedAt: nowIso()
    };
}

export function ensureActiveChatThread(chatState = {}, createThread = () => createChatThread()) {
    const activeId = chatState.activeThreadId;
    const threads = chatState.threads && typeof chatState.threads === "object"
        ? chatState.threads
        : (chatState.threads = {});
    if (activeId && threads[activeId]) {
        if (!Array.isArray(threads[activeId].messages)) {
            threads[activeId].messages = [];
        }
        chatState.messages = threads[activeId].messages;
        return threads[activeId];
    }
    const thread = createThread();
    threads[thread.id] = thread;
    chatState.activeThreadId = thread.id;
    chatState.messages = thread.messages;
    return thread;
}

export function getActiveChatMessages(chatState = {}, createThread = () => createChatThread()) {
    return ensureActiveChatThread(chatState, createThread).messages;
}

export function createIdGenerator(deps = {}) {
    const {
        now = defaultNow,
        start = 0
    } = deps;
    let counter = Number.isFinite(start) ? Number(start) : 0;
    return function genId(prefix = "id") {
        counter += 1;
        return `${prefix}-${now().toString(36)}-${counter}`;
    };
}

export function createEmptyProject(deps = {}) {
    const {
        cloneDefaultActuatorTypeCatalog = () => [],
        cloneDefaultSignalCategories = () => [],
        projectVersion = DEFAULT_PROJECT_VERSION
    } = deps;
    return {
        version: projectVersion,
        name: DEFAULT_PROJECT_NAME,
        selectedPlcProfileId: DEFAULT_PLC_PROFILE_ID,
        units: { distanceLinear: "mm", accelLinear: "m/s2", speedLinear: "mm/s" },
        axes: [],
        actuatorTypeCatalog: cloneDefaultActuatorTypeCatalog(),
        signalCategories: cloneDefaultSignalCategories(),
        actuators: [],
        signals: [],
        plcProfiles: [],
        plcProfilesSeeded: false,
        flow: { drawflowData: {}, subprocesses: [], internalFlags: [] }
    };
}

export function createInitialAppState(deps = {}) {
    const {
        createProject = () => createEmptyProject(),
        createThread = () => createChatThread(),
        createEmptyAgentSession = () => null
    } = deps;
    const initialChatThread = createThread();
    const agentSession = createEmptyAgentSession();
    const state = {
        accountSettings: null,
        project: createProject(),
        selectedAxisId: null,
        selectedMotionId: null,
        computed: {
            motionTimes: {},
            subprocessTimes: {},
            errors: [],
            motionErrors: {}
        },
        drawflow: null,
        nodeIdToMotionId: {},
        motionIdToNodeId: {},
        selectedFlowNodeId: null,
        pendingFlowConnection: null,
        activeFlowModuleName: "Home",
        suspendFlowEvents: false,
        pxPerSec: 120,
        openDetailAxes: new Set(),
        ui: {
            activeView: "timing",
            sidebarCollapsed: false,
            axisPopoverOpen: false,
            flowConnectionMode: "drag",
            flowConnectionHoverText: ""
        },
        chat: {
            activeThreadId: initialChatThread.id,
            threads: {
                [initialChatThread.id]: initialChatThread
            },
            messages: initialChatThread.messages,
            busy: false,
            mode: "st",
            dialect: DEFAULT_PLC_PROFILE_ID,
            plcProfileId: DEFAULT_PLC_PROFILE_ID,
            lastResult: "",
            uiOpen: true,
            settingsOpen: false,
            statusText: DEFAULT_STATUS_TEXT,
            settingsSummary: DEFAULT_SETTINGS_SUMMARY,
            inputPlaceholder: DEFAULT_INPUT_PLACEHOLDER,
            plcProfileEditor: {
                open: false,
                draft: null,
                error: ""
            },
            taskHistoryOpen: false,
            historyItems: [],
            historyLoading: false,
            workLogCollapsed: {},
            questionDraftInput: "",
            questionCardError: "",
            activeHistorySessionId: "",
            lastResumeAnnouncementSessionId: "",
            agentSession,
            planSession: agentSession
        }
    };
    return state;
}
